import React, { useState } from 'react';
import { X, Star, Clock, TrendingUp, Play } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useCategories } from '../hooks/useCategories';
import { useHabitSessionStore } from '../stores/habitSessionStore';
import { formatPoints, calculateLevelBasedPoints, POINTS } from '../utils/levelCalculation';
import type { Habit } from '../types';

interface HabitSessionReportProps {
  habit: Habit;
  durationSeconds: number;
  xpEarned: number;
  sessionType: 'timer' | 'stopwatch';
  onClose: () => void;
  onStartAnother: () => void;
}

export function HabitSessionReport({
  habit,
  durationSeconds,
  xpEarned,
  sessionType,
  onClose,
  onStartAnother
}: HabitSessionReportProps) {
  const [sessionNotes, setSessionNotes] = useState('');
  const { getCategoryColor, getCategoryName } = useCategories();
  const { getHabitSessions, userLevel } = useHabitSessionStore();

  const categoryColor = getCategoryColor(habit.category);
  const HabitIcon = habit.icon && (Icons[habit.icon as keyof typeof Icons] as any)
    ? (Icons[habit.icon as keyof typeof Icons] as any)
    : Icons.Star;

  const habitSessions = getHabitSessions(habit.id);
  const completedSessions = habitSessions.filter(s => s.status === 'completed');

  const averageDuration = completedSessions.length > 0
    ? completedSessions.reduce((sum, s) => sum + s.durationSeconds, 0) / completedSessions.length
    : 0;

  const longestSession = completedSessions.length > 0
    ? Math.max(...completedSessions.map(s => s.durationSeconds))
    : 0;

  const totalTime = completedSessions.reduce((sum, s) => sum + s.durationSeconds, 0) + durationSeconds;

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const baseHabitXP = calculateLevelBasedPoints(POINTS.HABIT, userLevel);
  const timeBasedXP = durationSeconds >= 60 ? Math.floor(durationSeconds / 60) * 5 : 0;
  const isNewLongest = durationSeconds > longestSession;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-lg w-full border border-border max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div
                className="p-3 rounded-lg"
                style={{ backgroundColor: `${categoryColor}15` }}
              >
                <HabitIcon className="w-6 h-6" style={{ color: categoryColor }} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Session Complete!
                </h2>
                <p className="text-sm text-muted-foreground">
                  {habit.name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-muted rounded-md transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {isNewLongest && (
            <div
              className="mb-6 p-4 rounded-lg border-2"
              style={{
                backgroundColor: `${categoryColor}10`,
                borderColor: categoryColor
              }}
            >
              <p className="font-semibold flex items-center gap-2" style={{ color: categoryColor }}>
                <TrendingUp className="w-5 h-5" />
                New Personal Best!
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                This is your longest session for this habit.
              </p>
            </div>
          )}

          <div className="mb-6 p-6 bg-muted rounded-lg text-center">
            <div className="text-5xl font-bold mb-2" style={{ color: categoryColor }}>
              {formatPoints(xpEarned)}
            </div>
            <p className="text-sm text-muted-foreground mb-4">XP Earned</p>

            <div className="space-y-2 text-sm">
              {durationSeconds >= 60 ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time-based XP:</span>
                    <span className="font-semibold text-foreground">
                      {Math.floor(durationSeconds / 60)} min × 5 = {formatPoints(timeBasedXP)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Base completion XP:</span>
                    <span className="font-semibold text-foreground">
                      {formatPoints(baseHabitXP)}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-border flex justify-between">
                    <span className="font-semibold text-foreground">Total:</span>
                    <span className="font-bold" style={{ color: categoryColor }}>
                      {formatPoints(xpEarned)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base completion XP:</span>
                  <span className="font-semibold" style={{ color: categoryColor }}>
                    {formatPoints(xpEarned)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Duration</span>
              </div>
              <p className="text-xl font-semibold text-foreground">
                {formatTime(durationSeconds)}
              </p>
            </div>
            <div className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-2 mb-1">
                <Star className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Session Type</span>
              </div>
              <p className="text-xl font-semibold text-foreground capitalize">
                {sessionType}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">
              Session Statistics
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total sessions:</span>
                <span className="font-semibold text-foreground">
                  {completedSessions.length + 1}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Average duration:</span>
                <span className="font-semibold text-foreground">
                  {formatTime(Math.round((averageDuration * completedSessions.length + durationSeconds) / (completedSessions.length + 1)))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Longest session:</span>
                <span className="font-semibold text-foreground">
                  {formatTime(Math.max(longestSession, durationSeconds))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total time invested:</span>
                <span className="font-semibold text-foreground">
                  {formatTime(totalTime)}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Session Notes (Optional)
            </label>
            <textarea
              value={sessionNotes}
              onChange={(e) => setSessionNotes(e.target.value)}
              placeholder="How did this session go? Any insights?"
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm resize-none"
              rows={3}
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={onStartAnother}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg font-semibold transition-colors"
              style={{
                borderColor: categoryColor,
                color: categoryColor
              }}
            >
              <Play className="w-4 h-4" />
              Start Another
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-colors"
              style={{ backgroundColor: categoryColor }}
            >
              Complete & Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
